import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlassCard } from '@/components/ui/layout/card';
import { CardContent } from '@/components/ui/layout/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/form/input';
import { Label } from '@/components/ui/form/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/overlay/dialog';
import { Mail, Lock, HelpCircle, Shield, ArrowLeft, AlertCircle, Info, FileText, Eye, EyeOff } from 'lucide-react';
import { loginWithEmail } from '@/lib/auth';
import { useAuth } from '@/context/AuthContext';
import { initiateGoogleConnect, initiateStravaConnect } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { isNative } from '@/lib/platform';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { Logo } from '@/components/Logo';
import { Alert, AlertDescription } from '@/components/ui/feedback/alert';
import { log } from '@/lib/logger';

export default function Login() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email || !password) {
      setError(t('login.errors.emailAndPassword'));
      return;
    }

    setIsLoading(true);
    
    try {
      // CRITICAL: Enforce token → /me ordering
      // Step 1: Store token
      await loginWithEmail(email, password);
      
      // Step 2: Call /me to validate token and get user
      // This ensures we have user data (including onboarding_complete) before routing
      await refreshUser();
      
      // Step 3: Get updated user from context after refresh
      // Wait a tick for React to update context
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Step 4: Route based on onboarding status
      // CRITICAL: Only navigate to today if onboarding is complete
      // Otherwise, AuthLanding will redirect to onboarding
      navigate('/today');
    } catch (err: unknown) {
      const apiError = err as { status?: number; message?: string; details?: { error?: string; reason?: string } | string | unknown };
      
      // Extract error details from backend response
      // Backend returns: {"error": "error_code", "message": "..."} or {"detail": {"error": "...", "message": "..."}}
      let errorReason: string | undefined;
      let errorMessage: string | undefined;
      
      if (apiError.details) {
        if (typeof apiError.details === 'object' && apiError.details !== null) {
          const details = apiError.details as { error?: string; reason?: string; message?: string };
          errorReason = details.reason || details.error;
          errorMessage = details.message;
        }
      }
      
      // Map specific error reasons to user-friendly messages
      if (apiError.status === 404 || errorReason === "user_not_found") {
        setError(t('login.errors.noAccount'));
      } else if (errorReason === "invalid_auth_method") {
        setError(t('login.errors.useGoogle'));
      } else if (errorReason === "invalid_credentials" || apiError.status === 401) {
        // Show backend message if available, otherwise generic message
        setError(errorMessage || t('login.errors.invalidCredentials'));
      } else {
        // Use backend message if available, otherwise generic error
        setError(errorMessage || apiError.message || t('login.errors.generic'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await initiateGoogleConnect();
    } catch (err) {
      const msg = extractErrorMessage(err, t('login.errors.googleFailed'));
      log.error('AUTH', 'Google connection error:', msg, err);
      setError(msg);
    }
  };

  const handleStravaLogin = async () => {
    try {
      await initiateStravaConnect();
    } catch (err) {
      const msg = extractErrorMessage(err, t('login.errors.stravaFailed'));
      log.error('AUTH', 'Strava connection error:', msg, err);
      setError(msg);
    }
  };

  const handleAppleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { loginWithAppleNative } = await import('@/lib/apple-auth-native');
      await loginWithAppleNative();
      await refreshUser();
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/today');
    } catch (err) {
      const msg = extractErrorMessage(err, t('login.errors.appleFailed'));
      log.error('AUTH', 'Apple connection error:', msg, err);
      // If user cancelled, don't show error
      if (msg.includes("cancelled") || msg.includes("cancel")) {
        // User intentionally cancelled — no error needed
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isCapacitor = isNative();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast({
        title: t('login.toasts.emailRequiredTitle'),
        description: t('login.toasts.emailRequiredDesc'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    
    // Placeholder - password reset not implemented yet
    toast({
      title: t('login.toasts.comingSoonTitle'),
      description: t('login.toasts.comingSoonDesc'),
    });
    setIsLoading(false);
    setShowForgotPassword(false);
  };

  return (
    <div className="min-h-[100svh] flex flex-col">
      {/* Header */}
      <header 
        className="px-4 pb-4 pt-4 flex items-center justify-between"
        style={{
          paddingTop: 'calc(1rem + env(safe-area-inset-top, 20px))',
          minHeight: 'calc(3.5rem + env(safe-area-inset-top, 20px))',
        }}
      >
        <div className="flex items-center">
          <Logo className="h-8 w-auto" />
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/about">
            <Button variant="ghost" size="sm" className="text-xs sm:text-sm">
              <Info className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('login.about')}</span>
            </Button>
          </Link>
          <Link to="/faq">
            <Button variant="ghost" size="sm" className="text-xs sm:text-sm">
              <HelpCircle className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('login.faq')}</span>
            </Button>
          </Link>
          <Link to="/privacy">
            <Button variant="ghost" size="sm" className="text-xs sm:text-sm">
              <Shield className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('login.privacy')}</span>
            </Button>
          </Link>
          <Link to="/terms">
            <Button variant="ghost" size="sm" className="text-xs sm:text-sm">
              <FileText className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{t('login.terms')}</span>
            </Button>
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-[clamp(1.5rem,4vw,1.875rem)] font-bold text-primary">{t('login.welcome')}</h1>
            <p className="text-muted-foreground">
              {t('login.subtitle')}
            </p>
          </div>

          <GlassCard className="border-border/50 shadow-lg">
            <CardContent className="space-y-4 pt-6">
              {/* Google OAuth */}
              <Button
                className="w-full"
                variant="outline"
                size="lg"
                onClick={handleGoogleLogin}
                disabled={isLoading}

              >
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t('login.continueWithGoogle')}
              </Button>

              {/* Apple Sign In — only on native iOS */}
              {isCapacitor && (
                <Button
                  className="w-full bg-black text-white hover:bg-black/90 border-none"
                  variant="outline"
                  size="lg"
                  onClick={handleAppleLogin}
                  disabled={isLoading}
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="white">
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                  </svg>
                  {t('login.continueWithApple')}
                </Button>
              )}

              {/* Strava OAuth */}
              <Button
                className="w-full bg-[#FC4C02] text-white hover:bg-[#FC4C02]/90 border-none"
                variant="outline"
                size="lg"
                onClick={handleStravaLogin}
                disabled={isLoading}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="white">
                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                </svg>
                {t('login.continueWithStrava')}
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">{t('login.orSignInWithEmail')}</span>
                </div>
              </div>

              {/* Email Login Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="signin-email">{t('login.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      className="pl-10"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="signin-password">{t('login.password')}</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-accent hover:underline disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {t('login.forgotPassword')}
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="signin-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setError(null);
                      }}
                      className="pl-10 pr-10"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? t('login.signingIn') : t('login.signIn')}
                </Button>
              </form>

              <div className="text-center text-sm text-muted-foreground">
                {t('login.noAccount')}{' '}
                <Link to="/signup" className="text-accent hover:underline font-medium">
                  {t('login.signUp')}
                </Link>
              </div>
            </CardContent>
          </GlassCard>

          <p className="text-center text-sm text-muted-foreground">
            {t('login.needHelp')}{' '}
            <Link to="/faq" className="text-accent hover:underline">
              {t('login.checkFaq')}
            </Link>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-xs text-muted-foreground">
        {t('login.copyright', { year: new Date().getFullYear() })}
      </footer>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('login.resetTitle')}</DialogTitle>
            <DialogDescription>
              {t('login.resetDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-email">{t('login.emailAddress')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowForgotPassword(false)}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('login.back')}
              </Button>
              <Button type="submit" className="flex-1" disabled={isLoading}>
                {isLoading ? t('login.sending') : t('login.sendResetLink')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
